import { Trash2 } from 'lucide-react'
import { formatCurrency } from '@utils'
import { ImageSize } from '@/constants'
import type { CartItem } from '@stores/useCartStore'
import OmpDrawer from './OmpDrawer'
import OmpCrossSellImage from '../components/OmpCrossSellImage'
import OmpQuantityStepper from '../components/OmpQuantityStepper'
import { DEFAULT_MAX_QUANTITY, cartLineTotal, cartTotals } from '../utils/crossSell'

interface OmpCrossSellCartDrawerProps {
  isOpen: boolean
  cart: CartItem[]
  busy: boolean
  onQuantityChange: (index: number, quantity: number) => void
  onRemove: (index: number) => void
  onCheckout: () => void
  onClose: () => void
}

const lineKey = (item: CartItem, index: number) =>
  `${item.campaign_product.id}-${item.variant?.id ?? 0}-${item.related_product?.id ?? 0}-${index}`

// The whole session cart, priced as the backend returned it after the last sync.
const OmpCrossSellCartDrawer = ({
  isOpen,
  cart,
  busy,
  onQuantityChange,
  onRemove,
  onCheckout,
  onClose,
}: OmpCrossSellCartDrawerProps) => {
  const totals = cartTotals(cart)
  const currency = cart[0]?.campaign_product.currency

  return (
    <OmpDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Review Added Items"
      forceDrawer
      className="omp-crosssell-overlay">
      <div className="omp-review-added">
        <p className="omp-review-added-intro">Review your items before checkout</p>

        <section className="omp-review-added-group">
          {cart.map((item, index) => {
            const { price, original } = cartLineTotal(item)
            const name = item.variant?.name || item.campaign_product.name
            return (
              <div key={lineKey(item, index)} className="omp-review-added-row">
                <div className="omp-review-added-thumb">
                  <OmpCrossSellImage source={item.campaign_product} size={ImageSize.Large} alt={name} />
                </div>
                <div className="omp-review-added-body">
                  <p className="omp-review-added-name">{name}</p>
                  <p className="omp-review-added-price">
                    {original != null && (
                      <span className="omp-crosssell-was">
                        {formatCurrency(original, item.campaign_product.currency)}
                      </span>
                    )}
                    <span className={original != null ? 'omp-crosssell-now' : 'omp-crosssell-regular'}>
                      {formatCurrency(price, item.campaign_product.currency)}
                    </span>
                  </p>
                  <OmpQuantityStepper
                    value={item.quantity}
                    max={item.campaign_product.max_quantity || DEFAULT_MAX_QUANTITY}
                    label={`Quantity of ${name}`}
                    disabled={busy}
                    onChange={quantity => onQuantityChange(index, quantity)}
                  />
                </div>
                <button
                  type="button"
                  className="omp-review-added-remove"
                  aria-label={`Remove ${name}`}
                  disabled={busy}
                  onClick={() => onRemove(index)}>
                  <Trash2 size={18} />
                </button>
              </div>
            )
          })}
        </section>

        <dl className="omp-review-added-totals">
          <div className="omp-review-added-total-row">
            <dt>
              Subtotal ({totals.itemCount} {totals.itemCount === 1 ? 'item' : 'items'})
            </dt>
            <dd>{formatCurrency(totals.subtotal, currency)}</dd>
          </div>
          {totals.savings > 0 && (
            <div className="omp-review-added-total-row omp-review-added-savings">
              <dt>You save {totals.savingsPercent}%</dt>
              <dd>-{formatCurrency(totals.savings, currency)}</dd>
            </div>
          )}
          <div className="omp-review-added-total-row omp-review-added-grand">
            <dt>Total</dt>
            <dd>{formatCurrency(totals.subtotal, currency)}</dd>
          </div>
        </dl>
        <p className="omp-review-added-note">Shipping and taxes are calculated at checkout.</p>

        <div className="omp-review-added-actions">
          <button
            type="button"
            className="omp-crosssell-btn omp-crosssell-btn-fill"
            disabled={busy || cart.length === 0}
            onClick={onCheckout}>
            Checkout
          </button>
          <button type="button" className="omp-crosssell-btn omp-crosssell-btn-outline" onClick={onClose}>
            Continue Shopping
          </button>
        </div>
      </div>
    </OmpDrawer>
  )
}

export default OmpCrossSellCartDrawer
